import * as React from 'react';

import { LogEvent } from '@analytics';
import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { useGetAcademy, useGetUser } from '@db/collections';
import { atom, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { isAppApproaching } from 'shared-values';
import { cn } from 'tailwind-config';
import { useOnClickOutside } from 'usehooks-ts';

import { StaticIcon } from '@components/Activation/written-test/components/StaticIcon';

import { api } from '@apis/hc';

const Spinner = () => (
  <div role="status">
    <svg
      aria-hidden="true"
      className="fill-new-white-100 text-new-gray-600 h-32 w-32 animate-spin"
      viewBox="0 0 100 101"
      fill="none"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentFill"
      />
    </svg>
    <span className="sr-only">Loading...</span>
  </div>
);

function ChannelTalkTrigger({
  children,
  pageName,
  tags = [],
  className,
  academyId,
}: {
  children?: ReactNode;
  pageName: string;
  tags?: string[];
  className?: string;
  academyId?: string;
}) {
  const router = useRouter();
  const pagePath = router.asPath.split('?')[0];
  const ref = React.useRef<HTMLButtonElement>(null);
  const { user } = useGetUser();
  const academy = useGetAcademy(academyId ?? (router.query.id as string));
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showLoading, setShowLoading] = React.useState(false); // 로딩 UI 상태

  React.useEffect(() => {
    ChannelService.onBadgeChanged((unread) => {
      setUnreadCount(unread);
    });
  }, []);

  useOnClickOutside(ref as unknown as React.RefObject<HTMLElement>, () => {
    ChannelService.hideMessenger();
  });

  const handleChannelButtonClick = async () => {
    if (isLoading) return;
    setIsLoading(true);

    // 0.3초 후에 로딩 UI 표시
    const timer = setTimeout(() => setShowLoading(true), 300);

    try {
      ChannelService.setPage(pagePath);

      LogEvent.활성화.inquiryClick(academy.data);

      const academyName = academy.data?.name ?? '';
      const locationAttachedName = academy.data?.locationAttachedName ?? '';
      const userName = user ? (user.realName?.length ? user.realName : user.name) : '유저정보없음';

      const name = pageName.includes('학원상세')
        ? `학원문의[${userName}][${locationAttachedName}]`
        : pageName;
      const channelCookie = document.cookie.split('; ').find((row) => row.startsWith('ch-veil-id'));
      const channelId = channelCookie?.split('=')[1] ?? '';

      if (channelId.length) {
        const result = await api.chat['get-user-chat-by-token'].$get({
          query: { token: channelId, pageName },
        });
        const data = (await result.json()).data;
        if (data) {
          setIsLoading(false);
          setShowLoading(false);
          clearTimeout(timer);
          ChannelService.openChat(data.id);
          return;
        }
      }

      ChannelService.addTags([...(pageName ? [pageName] : []), ...tags]);

      ChannelService.updateUser(
        {
          language: 'ko',
          profile: {
            email: user?.email ?? '',
            mobileNumber: user?.phoneNum ?? '',
            name,
            uid: user?.uid ?? '',
            academyId: academy.data?.id ?? '',
            academyName,
            locationAttachedName,
            userName,
            pageName: pageName ?? 'undefined',
          },
        },
        () => {
          ChannelService.openChat();
        },
      );
    } finally {
      setIsLoading(false);
      setShowLoading(false); // 로딩 종료
      clearTimeout(timer);
    }
  };

  const isAppApproach = isAppApproaching();

  if (React.isValidElement(children))
    return React.cloneElement(children, { onClick: handleChannelButtonClick, ref } as any);

  return (
    <button
      type="button"
      ref={ref}
      onClick={handleChannelButtonClick}
      className={cn(
        'w-54 h-54 text-new-white text-new-Caption2-bold bg-new-black-75 fixed right-[16px] z-20 flex flex-col items-center justify-center rounded-[18px] shadow-[0_0_8px_rgba(0,0,0,0.1)]',
        isAppApproach ? 'bottom-[110px]' : 'bottom-[88px]',
        className,
      )}
    >
      {showLoading ? (
        <Spinner />
      ) : (
        <>
          {unreadCount > 0 ? (
            <span className="text-new-Caption2-bold text-new-white bg-new-DTRed-400 absolute -right-2 -top-2 h-16 w-16 rounded-full">
              {unreadCount}
            </span>
          ) : null}
          <StaticIcon icon="headphone-outlined" width={24} height={24} className="fill-new-white" />
          학원문의
        </>
      )}
    </button>
  );
}

export const channelTalkAtom = atom(false);

export function ChannelTalkButton({
  children,
  ...props
}: React.ComponentProps<typeof ChannelTalkTrigger>) {
  const setIsChannelTalkOpen = useSetAtom(channelTalkAtom);

  React.useEffect(() => {
    const loadChannel = async () => {
      ChannelService.loadScript();
      ChannelService.boot({
        pluginKey: process.env.NEXT_PUBLIC_CHANNEL_IO_KEY ?? '',
        hideChannelButtonOnBoot: true,
      });

      ChannelService.onShowMessenger(() => {
        setIsChannelTalkOpen(true);
      });

      ChannelService.onHideMessenger(() => {
        setIsChannelTalkOpen(false);
      });
    };

    loadChannel();
  }, [setIsChannelTalkOpen]);

  return <ChannelTalkTrigger {...props}>{children}</ChannelTalkTrigger>;
}
